import {
  computeAmount,
  computePointsAmount,
  countUpAmount,
  countUpValue,
  wholeAmount,
} from '@captaincause/lib/amount'
import { getImageSrc } from '@captaincause/lib/getImageSrc'
import diftMedal from '../assets/dift-medal.svg'
import diftTexture from '../assets/dift-texture.webp'
import { tm } from '../tm'
import type { CountUpProps } from '@captaincause/lib/amount'
import type {
  Amount,
  Campaign,
  Currency,
  Dift as DiftType,
  Locales,
  Project,
} from '@captaincause/types'
import type { CSSProperties, ReactNode } from 'react'

type DiftProps = {
  id?: string
  campaign?: Campaign | undefined
  dift?: DiftType
  className?: string
  amountClassName?: string
  currencyClassName?: string
  backgroundColor?: string
  withoutBackgroundTexture?: boolean
  children: ReactNode
  style?: CSSProperties
}

export function Dift({
  id = undefined,
  campaign = undefined,
  dift = undefined,
  backgroundColor = undefined,
  withoutBackgroundTexture = false,
  className = '',
  children,
  style = undefined,
}: DiftProps) {
  if (!children) return null

  const bgColor = campaign?.button_color || backgroundColor || '#F7F8F2'

  let backgroundTexture: string | undefined = getImageSrc(diftTexture)
  if (withoutBackgroundTexture) backgroundTexture = undefined

  return (
    <div
      id={id}
      data-testid="ccui-dift"
      style={{
        ...(style || {}),
        ...(backgroundTexture
          ? {
              background: `padding-box url(${backgroundTexture}) center/contain, padding-box linear-gradient(${bgColor}, ${bgColor}) center/contain, linear-gradient(to bottom, #FFFFFF, #00000000) border-box center/contain, radial-gradient(#ffffff00, ${bgColor}) border-box center/105%`,
            }
          : {}),
      }}
      className={tm(
        'flex h-[144px] w-[220px] items-center justify-center rounded-l-[75px] rounded-r-[274px] border-[3px] border-transparent md:h-[180px] md:w-[294px] md:border-[4px]',
        className,
      )}
    >
      {children}
    </div>
  )
}

type DiftWithAmountProps = Omit<DiftProps, 'children'> & {
  amount?: Amount
  color?: string
  countUpOptions?: CountUpProps
  locale: Locales
  pointsMode?: boolean
  pointsValueInCents?: number | null
  skipCountUp?: boolean
  dift?: DiftType
}

const defaultAmount = { amount: 0, currency: 'EUR' as Currency }
export function DiftWithAmount({
  id = undefined,
  campaign,
  dift = undefined,
  pointsMode = false,
  pointsValueInCents = null,
  amount = defaultAmount,
  className = '',
  amountClassName = '',
  currencyClassName = '',
  countUpOptions = undefined,
  locale,
  backgroundColor = undefined,
  color = undefined,
  skipCountUp = false,
}: DiftWithAmountProps) {
  const [, currency] = computeAmount(amount, locale).split(' ')
  const displayedValue = pointsMode
    ? computePointsAmount(amount, pointsValueInCents)
    : wholeAmount(amount)
  const displayedValueLength = displayedValue.toString().length

  let textColor: string | undefined =
    campaign?.button_text_color || color || '#FFFFFF'
  if (dift) {
    // Text color will be passed through the classname.
    textColor = undefined
  }

  return (
    <Dift
      id={id}
      campaign={campaign}
      dift={dift}
      className={className}
      backgroundColor={backgroundColor}
    >
      {amount.amount > 0 ? (
        <div
          className={
            pointsMode ? '-ml-4 flex flex-col items-center space-y-0' : ''
          }
        >
          <span
            style={{ color: textColor }}
            className={tm(
              'font-title text-[56px] font-extrabold md:text-[56px]',
              pointsMode && 'leading-[1]',
              displayedValueLength > 3 && 'text-[48px]',
              displayedValueLength > 4 && 'text-[40px]',
              displayedValueLength > 5 && 'text-[32px] md:text-[50px]',
              displayedValueLength > 6 && 'text-[28px] md:text-[40px]',
              amountClassName,
            )}
          >
            {pointsMode
              ? countUpValue({
                  value: computePointsAmount(amount, pointsValueInCents),
                  locale,
                  countUpOptions: {
                    ...countUpOptions,
                    start: skipCountUp ? displayedValue : 0,
                  },
                })
              : countUpAmount({
                  amount,
                  locale,
                  numberOnly: true,
                  countUpOptions: {
                    ...countUpOptions,
                    start: skipCountUp ? displayedValue : 0,
                  },
                })}
          </span>
          <span
            style={{ color: textColor }}
            className={tm(
              'ml-2 text-[32px] font-semibold opacity-50',
              pointsMode && 'ml-0 text-center !text-[18px] font-medium',
              currencyClassName,
            )}
          >
            {pointsMode ? 'points' : currency}
          </span>
        </div>
      ) : null}
    </Dift>
  )
}

type DiftVersoProps = Omit<DiftProps, 'children'> & {
  project: Project
}
export function DiftVerso({
  campaign = undefined,
  className = '',
  project,
}: DiftVersoProps) {
  const medal = getImageSrc(diftMedal)

  return (
    <Dift campaign={campaign} className={tm(className)}>
      <div className="relative">
        <div className="rounded-lg bg-white p-1">
          <img
            src={project.organization?.logo}
            alt={project.organization?.name}
            width={88}
            height={88}
            className="h-[88px] rounded-lg object-contain"
          />
        </div>
        <img
          src={medal}
          alt=""
          className="absolute -bottom-[13px] -right-[13px]"
        />
      </div>
    </Dift>
  )
}
